import OccluUpperWorker from 'worker-loader?filename=static/js/[name].js!../../workers/occluUpper.worker';
import OccluLowerWorker from 'worker-loader?filename=static/js/[name].js!../../workers/occluLower.worker';
import { Mesh } from '../model/Mesh';
import Util from '../shared/util';
import vtkLookupTable from 'vtk.js/Sources/Common/Core/LookupTable';

import { OccluUtil, OccluWorkerInMsg, OccluWorkerOutMsg, OccluSelectFlag, WasmOccluCmd } from '../shared/OccluUtil';

class OcclusionMapping {
  // compute upper jaw pressure in this worker
  private upperWorker: OccluUpperWorker;
  // compute lower jaw pressure in this worker
  private lowerWorker: OccluLowerWorker;
  private isProcessing: boolean;
  private onProcessSuccess: () => void;
  private onProcessFail: () => void;
  private onWasmAvailable: () => void;
  private upperMesh: Mesh;
  private lowerMesh: Mesh;
  private lut: vtkLookupTable;
  private occluResult: OccluWorkerOutMsg;
  private isUpperAvailable: boolean;
  private isLowerAvailable: boolean;

  constructor() {
    this.upperWorker = new OccluUpperWorker();
    this.upperWorker.onmessage = (evt: any) => {
      this.onMessageFromWorker(evt);
    };
    this.lowerWorker = new OccluLowerWorker();
    this.lowerWorker.onmessage = (evt: any) => {
      this.onMessageFromWorker(evt);
    };
    this.upperWorker.postMessage(WasmOccluCmd.InitWasm);
    this.lowerWorker.postMessage(WasmOccluCmd.InitWasm);
    this.isProcessing = false;
  }

  computeOcclusionPressure(upper: Mesh, lower: Mesh) {
    if (this.isProcessing || !upper || !lower) {
      return;
    }
    console.time('OcclusionMapping');
    this.upperMesh = upper;
    this.lowerMesh = lower;
    const msg = new OccluWorkerInMsg();
    msg.taskId = Util.formatCurrentDate();
    msg.upper = OccluUtil.toOccluWorkerMeshData(upper.actor);
    msg.lower = OccluUtil.toOccluWorkerMeshData(lower.actor);
    this.postToWorker(msg);
  }

  setOnProcessSuccess(cb: () => void) {
    this.onProcessSuccess = cb;
  }

  setOnProcessFail(cb: () => void) {
    this.onProcessFail = cb;
  }

  setOnWasmAvailable(cb: () => void) {
    this.onWasmAvailable = cb;
  }

  hasOccluResult() {
    return this.occluResult && this.occluResult.lowerScalars && this.occluResult.lowerScalars;
  }

  getUpperMesh() {
    return this.upperMesh;
  }

  getLowerMesh() {
    return this.lowerMesh;
  }

  private postToWorker(msg: OccluWorkerInMsg) {
    if (this.upperWorker && this.lowerWorker && msg.upper && msg.lower) {
      this.isProcessing = true;
      msg.selectFlag = OccluSelectFlag.UPPER;
      this.upperWorker.postMessage(msg);
      msg.selectFlag = OccluSelectFlag.LOWER;
      this.lowerWorker.postMessage(msg);
    }
  }

  private onMessageFromWorker(evt: any) {
    if (!this.checkWasmAvailable(evt?.data)) {
      return;
    }
    const tmp = evt?.data as OccluWorkerOutMsg;
    if (tmp) {
      if (!this.occluResult) {
        this.occluResult = new OccluWorkerOutMsg();
      }
      if (tmp.selectFlag == OccluSelectFlag.UPPER && tmp.upperScalars?.length > 0) {
        this.occluResult.upperScalars = tmp.upperScalars;
      }
      if (tmp.selectFlag == OccluSelectFlag.LOWER && tmp.lowerScalars?.length > 0) {
        this.occluResult.lowerScalars = tmp.lowerScalars;
      }
      if (this.occluResult.lowerScalars?.length > 0 && this.occluResult.upperScalars?.length > 0) {
        this.isProcessing = false;
        this.updateMesh(this.occluResult);
        console.timeEnd('OcclusionMapping');
      }
    } else {
      if (this.onProcessFail) {
        this.onProcessFail();
      }
    }
  }

  private updateMesh(msg: OccluWorkerOutMsg) {
    if (!this.lut) {
      this.lut = OccluUtil.createLut();
    }
    if (msg.upperScalars?.length > 0) {
      this.updateScalars(this.upperMesh.actor, msg.upperScalars);
    }
    if (msg.lowerScalars?.length > 0) {
      this.updateScalars(this.lowerMesh.actor, msg.lowerScalars);
    }
    if (this.onProcessSuccess) {
      this.onProcessSuccess();
    }
  }

  private updateScalars(actor: any, scalars: Float64Array) {
    OccluUtil.updatePressureScalars(actor, scalars);
    OccluUtil.setMapperScalarRange(actor);
    OccluUtil.setLookupTable(actor, this.lut);
  }

  private checkWasmAvailable(resp: any) {
    if (this.isWasmAvailable()) {
      return true;
    }
    if (!this.isLowerAvailable) {
      this.isLowerAvailable = WasmOccluCmd.InitWasmSuccessLower === resp;
    }
    if (!this.isUpperAvailable) {
      this.isUpperAvailable = WasmOccluCmd.InitWasmSuccessUpper === resp;
    }
    if (this.isWasmAvailable()) {
      this.onWasmAvailable?.();
      return true;
    }
    return false;
  }

  private isWasmAvailable() {
    return this.isLowerAvailable && this.isUpperAvailable;
  }
}

const occlusionMapping = new OcclusionMapping();

export default occlusionMapping;
